import { useLoginSpeckle } from "@/composables/useLoginSpeckle";
import { msalInstance } from "@/msalAuthConfig";
import vuetify from "@/plugins/vuetify";
import { waitForOffice } from "@/utils/office";
import { EventType } from "@azure/msal-browser";
import { createMsalVuePlugin } from "@haskoning/msal/vue";
import { VueSpecklePlugin, setSpeckleConfig, useSpeckleStore } from "@haskoning/vue-speckle";
import * as Sentry from "@sentry/vue";
import { createApp } from "vue";
import App from "./App.vue";
import { pinia } from "./modules/pinia";

function getAccessCodeFromUrl(rawUrl: string): string | null {
  return new URL(rawUrl).searchParams.get("access_code");
}

export async function relayAuthCallbackToTaskPane(accessCode: string): Promise<void> {
  const officeAvailable = await waitForOffice();

  if (!officeAvailable) {
    return;
  }

  await Office.onReady();

  if (!Office.context?.ui?.messageParent) {
    throw new Error("The Office dialog messaging API is unavailable.");
  }

  Office.context.ui.messageParent(JSON.stringify({ accessCode: accessCode }));
}

// This file starts in two different windows:
//   Excel task pane      (no query string)     -> start the app
//   Speckle login dialog (?access_code=...)    -> send the code to the task pane, then close
// Speckle redirects back to this same page, which is why the dialog ends up here too.
// It must not start the app: that window has its own memory and is about to close, so
// the token would be stored there and lost instead of reaching the task pane.
const dialogAccessCode = getAccessCodeFromUrl(globalThis.location.href);

const config = {
  serverUrl: import.meta.env.VITE_APP_SERVER_URL,
  workspaceId: import.meta.env.VITE_APP_SPECKLE_WORKSPACE_ID,
  appId: import.meta.env.VITE_APP_SPECKLE_ID,
  appSecret: import.meta.env.VITE_APP_SPECKLE_SECRET,
  appName: import.meta.env.VITE_APP_SPECKLE_NAME,
};

setSpeckleConfig(config.serverUrl, config.workspaceId, config.appId, config.appSecret, config.appName, () =>
  useLoginSpeckle().checkSpeckleSsoSession()
);

if (dialogAccessCode) {
  void relayAuthCallbackToTaskPane(dialogAccessCode);
} else {
  bootstrapTaskPane();
}

function bootstrapTaskPane(): void {
  const app = createApp(App).use(vuetify).use(pinia);
  app.use(createMsalVuePlugin(msalInstance));
  app.use(VueSpecklePlugin, {
    speckleStore: () => useSpeckleStore({ autoSelectData: false }),
    componentsAutoSelectData: false,
  });

  const apiOrigin = new URL(import.meta.env.VITE_API_URL || "http://localhost:8000").origin;
  const tracePropagationTargets = [globalThis.location.origin, apiOrigin];
  const enableSessionReplay = import.meta.env.MODE !== "production";

  Sentry.init({
    app,
    dsn: import.meta.env.VITE_APP_SENTRY_DSN,
    environment: import.meta.env.MODE,
    tracePropagationTargets,
    integrations: [
      ...(enableSessionReplay ? [Sentry.replayIntegration()] : []),
      Sentry.feedbackIntegration({
        triggerLabel: "Feedback",
        formTitle: "Send Feedback",
        submitButtonLabel: "Send Feedback",
        cancelButtonLabel: "Cancel",
        messagePlaceholder: "What would you like to tell us?",
        successMessageText: "Thank you for your feedback!",
        colorScheme: "light",
        showName: true,
        showEmail: true,
        enableScreenshot: true,
        autoInject: false,
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: 0.1, // Capture 10% of the transactions
    // Session Replay
    replaysSessionSampleRate: enableSessionReplay ? 0.1 : 0,
    replaysOnErrorSampleRate: enableSessionReplay ? 1.0 : 0,
  });

  app.mount("#app");

  const feedback = Sentry.getFeedback();
  if (feedback) {
    let widget: ReturnType<typeof feedback.createWidget> | null = null;

    const isAuthenticated = () => {
      const accounts = msalInstance.getAllAccounts();
      return accounts.length > 0;
    };

    const updateFeedbackWidget = () => {
      if (isAuthenticated() && !widget) {
        widget = feedback.createWidget();
        widget.appendToDom();

        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0 && accounts[0].username) {
          Sentry.setUser({
            username: accounts[0].name ?? "",
            email: accounts[0].username ?? "",
          });
        }
      } else if (!isAuthenticated() && widget) {
        widget.removeFromDom();
        widget = null;
        Sentry.setUser(null);
      }
    };

    updateFeedbackWidget();

    msalInstance.addEventCallback((event) => {
      if (
        event.eventType === EventType.LOGIN_SUCCESS ||
        event.eventType === EventType.LOGOUT_SUCCESS ||
        event.eventType === EventType.ACTIVE_ACCOUNT_CHANGED
      ) {
        updateFeedbackWidget();
      }
    });
  }
}
